// src/components/common/QRCodeDisplay.tsx

import React, { useRef, useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Stack,
    Menu,
    MenuItem,
    Button,
    TextField,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { 
    Download,
    Print,
    Share,
    ContentCopy,
    ArrowDropDown,
  
} from '@mui/icons-material';
import { useNotify, useTranslate, useDataProvider } from 'react-admin';
import * as QRCode from 'qrcode';
import type { jsPDF } from 'jspdf';

interface QRCodeDisplayProps {
    code: string;
    displayReference?: string;
    tagId?: string;
    assignmentId?: string;
    hotelId?: string;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
    code,
    displayReference,
    tagId,
    assignmentId,
    hotelId
}) => {
    const qrRef = useRef<HTMLImageElement>(null);
    const notify = useNotify();
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [qrUrl, setQrUrl] = useState<string>('');
    const [showSmsForm, setShowSmsForm] = useState<boolean>(false);
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [sendingSms, setSendingSms] = useState<boolean>(false);
    const [hotelName, setHotelName] = useState<string>('');



    useEffect(() => {
    const fetchHotelName = async () => {
        if (!hotelId) return;
        
        try {
            const response = await dataProvider.getOne('hotels', { id: hotelId });
            if (response.data && response.data.name) {
                setHotelName(response.data.name);
            }
        } catch (error) {
            console.warn('Could not fetch hotel name:', error);
            // No es crítico si falla, simplemente no mostramos el nombre
        }
    };
    
    fetchHotelName();
}, [hotelId, dataProvider]);


    useEffect(() => {
        if (code) {
            const opts: QRCode.QRCodeToDataURLOptions = {
                errorCorrectionLevel: 'H',
                type: 'image/png',
                // @ts-ignore - quality is valid but not in types
                quality: 0.9,
                margin: 1,
                width: 300
            };

            // Asegurar que el código esté en formato correcto
            let qrCode = code;
            if (!qrCode.includes('<')) {
                qrCode = '<' + qrCode + '>';
            }

            QRCode.toDataURL(qrCode, opts)
                .then(url => {
                    setQrUrl(url);
                })
                .catch(err => {
                    console.error('Error generating QR code:', err);
                    notify(translate('resources.tags.notifications.qr_generation_error'), { type: 'error' });
                });
        }
    }, [code, notify, translate]);

    const handleSendSms = () => {
        if (!tagId || !assignmentId || !hotelId) {
            notify(translate('resources.tags.notifications.sms_not_available'), { type: 'warning' });
            return;
        }
        setShowSmsForm(!showSmsForm); // Toggle del formulario
    };

    const handleSendSmsConfirm = async () => {
        if (!phoneNumber.trim()) {
            notify(translate('resources.tags.notifications.phone_required', 'Phone number is required'), { type: 'warning' });
            return;
        }

        setSendingSms(true);
        try {
            // @ts-ignore - sendTagBySms might be a custom method
            await dataProvider.sendTagBySms(
                assignmentId,
                tagId,
                phoneNumber.trim(),
                hotelId
            );
            notify(translate('resources.tags.notifications.sms_sent', 'SMS sent successfully'), { type: 'success' });
            setShowSmsForm(false);
            setPhoneNumber('');
        } catch (error) {
            notify(translate('resources.tags.notifications.sms_error', 'Failed to send SMS'), { type: 'error' });
        } finally {
            setSendingSms(false);
        }
    };

    const handleCancelSms = () => {
        setShowSmsForm(false);
        setPhoneNumber('');
    };

 const generatePDF = async (format: 'a4' | 'ticket' = 'a4') => {
    if (!qrUrl) {
        notify(translate('resources.tags.notifications.qr_not_ready'), { type: 'warning' });
        return;
    }
    
    try {
        const { jsPDF } = await import('jspdf');
        
        let pdf: jsPDF;
        let pageWidth: number, pageHeight: number, centerX: number;
        
        if (format === 'ticket') {
            pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [80, 200] });
            pageWidth = 80;
            pageHeight = 200;
        } else {
            pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            pageWidth = pdf.internal.pageSize.getWidth();
            pageHeight = pdf.internal.pageSize.getHeight();
        }
        
        centerX = pageWidth / 2;
        let currentY = format === 'ticket' ? 15 : 50; // Empezar más abajo en A4

        if (format === 'a4') {
            // Título principal sin fondo, elegante
            pdf.setTextColor(68, 68, 68);  // Gris oscuro
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'normal');
            
            // Texto en mayúsculas con espaciado (simulado)
            const titleText = translate('resources.tags.pdf.digital_locker_card').toUpperCase();
            pdf.text(titleText, centerX, currentY, { align: 'center' });
            
            // Línea decorativa debajo del título
            currentY += 8;
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.5);
            pdf.line(centerX - 40, currentY, centerX + 40, currentY);
            currentY += 15;
            
            // Nombre del hotel en cursiva (si está disponible)
            if (hotelName) {
                pdf.setTextColor(102, 102, 102);
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'italic');
                pdf.text(hotelName, centerX, currentY, { align: 'center' });
                currentY += 12;
            }
            
            // Display Reference más prominente
            if (displayReference) {
                pdf.setTextColor(44, 44, 44);
                pdf.setFontSize(16);
                pdf.setFont('helvetica', 'bold');
                pdf.text(displayReference, centerX, currentY, { align: 'center' });
                currentY += 20;
            }
            
            // QR Code con marco elegante
            const qrSize = 45; // Tamaño moderado
            const qrX = centerX - (qrSize / 2);
            
            // Marco del QR
            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.3);
            pdf.rect(qrX - 8, currentY - 8, qrSize + 16, qrSize + 16);
            
            // QR Code
            pdf.addImage(qrUrl, 'PNG', qrX, currentY, qrSize, qrSize);
            currentY += qrSize + 25;
            
            // Separador decorativo (tres puntos)
            pdf.setTextColor(200, 200, 200);
            pdf.setFontSize(14);
            pdf.text('• • •', centerX, currentY, { align: 'center' });
            currentY += 15;
            
            // Caja de instrucciones con borde sutil
            const boxX = 40;
            const boxWidth = pageWidth - 80;
            const instructionStartY = currentY;
            
            // Título de instrucciones
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(68, 68, 68);
            pdf.setFontSize(10);
            const instructionsTitle = translate('resources.tags.pdf.instructions_title').toUpperCase();
            pdf.text(instructionsTitle, centerX, currentY, { align: 'center' });
            currentY += 10;
            
            // Instrucciones centradas
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(85, 85, 85);
            pdf.setFontSize(9);
            
            const instructions = [
                translate('resources.tags.pdf.instruction_1'),
                translate('resources.tags.pdf.instruction_2'),
                translate('resources.tags.pdf.instruction_3'),
            ];
            
            instructions.forEach((instruction, index) => {
                const lines = pdf.splitTextToSize(`${index + 1}. ${instruction}`, boxWidth);
                lines.forEach((line) => {
                    pdf.text(line, centerX, currentY, { align: 'center' });
                    currentY += 5;
                });
                currentY += 2;
            });
            
            // Mensaje ambiental en cursiva
            currentY += 8;
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(119, 119, 119);
            pdf.setFontSize(8);
            const envMessage = translate('resources.tags.pdf.environmental_message');
            const envLines = pdf.splitTextToSize(envMessage, boxWidth);
            envLines.forEach((line) => {
                pdf.text(line, centerX, currentY, { align: 'center' });
                currentY += 4;
            });
            
            // Línea superior del mensaje ambiental
            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.2);
            pdf.line(centerX - 30, currentY - envLines.length * 4 - 6, centerX + 30, currentY - envLines.length * 4 - 6);
            
            // Contacto al final (parte inferior de la página)
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(68, 68, 68);
            pdf.setFontSize(9);
            pdf.text(translate('resources.tags.pdf.contact_help'), centerX, pageHeight - 30, { align: 'center' });
            
        } else {
            // Formato ticket (mantener el código original para ticket)
            pdf.setTextColor(51, 51, 51);
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text(translate('resources.tags.pdf.digital_locker_card'), centerX, currentY, { align: 'center' });
            currentY += 8;

            if (hotelName) {
                pdf.setTextColor(90, 90, 90);
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'normal');
                pdf.text(hotelName, centerX, currentY, { align: 'center' });
                currentY += 10;
            }

            if (displayReference) {
                pdf.setTextColor(74, 74, 74);
                pdf.setFontSize(14);
                pdf.setFont('helvetica', 'bold');
                pdf.text(displayReference, centerX, currentY, { align: 'center' });
                currentY += 12;
            }

            const qrSize = 35;
            const qrX = centerX - (qrSize / 2);
            
            pdf.setFillColor(255, 255, 255);
            pdf.rect(qrX - 5, currentY - 5, qrSize + 10, qrSize + 10, 'F');
            pdf.setDrawColor(230, 230, 230);
            pdf.setLineWidth(0.5);
            pdf.rect(qrX - 5, currentY - 5, qrSize + 10, qrSize + 10);
            
            pdf.addImage(qrUrl, 'PNG', qrX, currentY, qrSize, qrSize);
            currentY += qrSize + 20;

            const instructionFontSize = 8;
            
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(51, 51, 51);
            pdf.setFontSize(instructionFontSize + 1);
            pdf.text(translate('resources.tags.pdf.instructions_title'), centerX, currentY, { align: 'center' });
            currentY += 8;

            const instructions = [
                translate('resources.tags.pdf.instruction_1'),
                translate('resources.tags.pdf.instruction_2'),
                translate('resources.tags.pdf.instruction_3'),
            ];

            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(102, 102, 102);
            pdf.setFontSize(instructionFontSize);

            instructions.forEach((line) => {
                const lines = pdf.splitTextToSize(line, pageWidth - 20);
                lines.forEach((splitLine) => {
                    pdf.text(splitLine, 10, currentY);
                    currentY += 5;
                });
                currentY += 2;
            });

            currentY += 5;
            const envMessage = translate('resources.tags.pdf.environmental_message');
            const envLines = pdf.splitTextToSize(envMessage, pageWidth - 20);
            envLines.forEach((line) => {
                pdf.text(line, 10, currentY);
                currentY += 4;
            });

            currentY = pageHeight - 15;
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(51, 51, 51);
            pdf.setFontSize(instructionFontSize);
            pdf.text(translate('resources.tags.pdf.contact_help'), centerX, currentY, { align: 'center' });
        }

        const filename = `locker-access-${code}-${format}.pdf`;
        pdf.save(filename);
        notify(translate('resources.tags.notifications.pdf_downloaded'), { type: 'success' });
        
    } catch (error) {
        console.error('Error generating PDF:', error);
        notify(translate('resources.tags.notifications.pdf_error'), { type: 'error' });
    }
};

    const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDownload = (format: 'a4' | 'ticket') => {
        generatePDF(format);
        handleMenuClose();
    };

    const handlePrint = () => {
        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>${translate('resources.tags.print.page_title', { code })}</title>
                <style>
                    @media print {
                        body { margin: 0; }
                        .no-print { display: none; }
                    }
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        margin: 0;
                        padding: 20px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        min-height: 100vh;
                        background: white;
                    }
                    .container {
                        text-align: center;
                        max-width: 400px;
                        width: 100%;
                    }
                    .header {
                        background: linear-gradient(135deg, #0093B2, #53C3D2);
                        color: white;
                        padding: 20px;
                        border-radius: 15px;
                        margin-bottom: 30px;
                    }
                    .header h1 {
                        margin: 0;
                        font-size: 24px;
                        font-weight: 400;
                    }
                    .reference {
                        color: #0093B2;
                        font-size: 20px;
                        font-weight: bold;
                        margin-bottom: 20px;
                    }
                    .qr-container {
                        border: 2px solid #f0f0f0;
                        padding: 30px;
                        border-radius: 15px;
                        background: white;
                        margin-bottom: 30px;
                    }
                    .qr-code {
                        width: 200px;
                        height: 200px;
                        margin: 0 auto 20px;
                        border: 2px solid #f8f9fa;
                        padding: 10px;
                        background: white;
                    }
                    .instructions {
                        text-align: left;
                        margin: 20px 0;
                        padding: 20px;
                        background: #f8f9fa;
                        border-radius: 10px;
                    }
                    .instructions h3 {
                        color: #333;
                        font-size: 14px;
                        margin-bottom: 15px;
                    }
                    .instructions ol {
                        margin: 10px 0;
                        padding-left: 20px;
                        color: #666;
                        font-size: 12px;
                        line-height: 1.8;
                    }
                    .instructions li {
                        margin-bottom: 8px;
                    }
                    .environmental {
                        font-style: italic;
                        color: #666;
                        font-size: 11px;
                        margin-top: 15px;
                        padding-top: 15px;
                        border-top: 1px solid #ddd;
                    }
                    .contact {
                        font-weight: bold;
                        color: #333;
                        margin-top: 20px;
                        font-size: 12px;
                    }
                    .footer {
                        color: #999;
                        font-size: 10px;
                        margin-top: 20px;
                    }
                </style>
            </head>
            <body>
                <div class="container">
                     <div class="header">
                        <h1>${translate('resources.tags.pdf.digital_locker_card')}</h1>
                        ${hotelName ? `<div class="hotel-name">${hotelName}</div>` : ''}
                    </div>
                    
                    ${displayReference ? `<div class="reference">${displayReference}</div>` : ''}
                    
                    <div class="qr-container">
                        <img src="${qrUrl}" alt="QR Code" class="qr-code" />
                    </div>
                    
                    <div class="instructions">
                        <h3>${translate('resources.tags.pdf.instructions_title')}</h3>
                        <ol>
                            <li>${translate('resources.tags.pdf.instruction_1')}</li>
                            <li>${translate('resources.tags.pdf.instruction_2')}</li>
                            <li>${translate('resources.tags.pdf.instruction_3')}</li>
                          
                        </ol>
                        <div class="environmental">
                            ${translate('resources.tags.pdf.environmental_message')}
                        </div>
                    </div>
                    
                    <div class="contact">
                        ${translate('resources.tags.pdf.contact_help')}
                    </div>

                </div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(printContent);
            printWindow.document.close();
            
            printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
                setTimeout(() => printWindow.close(), 250);
            };
        }
    };

    const handleShare = async () => {
        if (navigator.share && navigator.canShare) {
            try {
                // Convertir la imagen base64 a blob
                const response = await fetch(qrUrl);
                const blob = await response.blob();
                const file = new File([blob], `qr-code-${code}.png`, { type: 'image/png' });
                
                if (navigator.canShare({ files: [file] })) {
                    await navigator.share({
                        title: translate('resources.tags.share.title', 'Locker Access QR'),
                        text: displayReference || code,
                        files: [file]
                    });
                } else {
                    // Si no puede compartir archivos, compartir el código
                    handleCopyImage();
                }
            } catch (err: any) {
                if (err.name !== 'AbortError') {
                    console.error('Share failed:', err);
                    handleCopyImage();
                }
            }
        } else {
            handleCopyImage();
        }
    };

    const handleCopyImage = async () => {
        try {
            // Convertir base64 a blob
            const response = await fetch(qrUrl);
            const blob = await response.blob();
            
            // Intentar copiar la imagen al portapapeles
            if (navigator.clipboard && window.ClipboardItem) {
                const item = new ClipboardItem({ 'image/png': blob });
                await navigator.clipboard.write([item]);
                notify(translate('resources.tags.notifications.qr_copied', 'QR image copied to clipboard'), { type: 'success' });
            } else {
                // Fallback: copiar el código si no se puede copiar la imagen
                navigator.clipboard.writeText(code).then(() => {
                    notify(translate('resources.tags.notifications.code_copied', 'Code copied to clipboard'), { type: 'success' });
                });
            }
        } catch (error) {
            console.error('Failed to copy image:', error);
            // Fallback: copiar el código
            navigator.clipboard.writeText(code).then(() => {
                notify(translate('resources.tags.notifications.code_copied', 'Code copied to clipboard'), { type: 'success' });
            });
        }
    };

    const handleCopy = handleCopyImage;

    if (!qrUrl) {
        return (
            <Box sx={{ p: 2, textAlign: 'center', height: '248px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>{translate('resources.locker-assignments.tags.generating_qr')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            textAlign: 'center'
        }}>
            <Box sx={{ mb: 2 }}>
                <img 
                    ref={qrRef}
                    src={qrUrl}
                    alt={`QR Code for ${code}`}
                    style={{ 
                        border: '2px solid #f0f0f0',
                        borderRadius: '8px',
                        padding: '8px',
                        backgroundColor: 'white',
                        width: '150px',
                        height: '150px'
                    }}
                />
            </Box>

            <Typography 
                variant="body2" 
                sx={{ 
                    mb: 2, 
                    letterSpacing: '2px', 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontFamily: 'monospace'
                }}
            >
                {code}
            </Typography>

            <Stack direction="row" spacing={1} justifyContent="center">
                <Tooltip title={translate('resources.tags.actions.download_pdf')}>
                    <IconButton onClick={handleDownloadClick} sx={{width: 48}}>
                        <Download fontSize="small" />
                        <ArrowDropDown fontSize="small" />
                    </IconButton>
                </Tooltip>
                
                <Tooltip title={translate('resources.tags.actions.print')}>
                    <IconButton size="small" onClick={handlePrint}>
                        <Print fontSize="small" />
                    </IconButton>
                </Tooltip>
                
                <Tooltip title={translate('resources.tags.actions.share_qr', 'Share QR')}>
                    <IconButton size="small" onClick={handleShare}>
                        <Share fontSize="small" />
                    </IconButton>
                </Tooltip>
                
                <Tooltip title={translate('resources.tags.actions.copy_qr', 'Copy QR image')}>
                    <IconButton size="small" onClick={handleCopy}>
                        <ContentCopy fontSize="small" />
                    </IconButton>
                </Tooltip>

                
            </Stack>

            <Dialog 
                open={showSmsForm} 
                onClose={handleCancelSms}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {translate('resources.tags.sms.title', 'Send QR code via SMS')}
                </DialogTitle>
                
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        fullWidth
                        label={translate('resources.tags.fields.phone_number', 'Phone Number')}
                        placeholder="+34612345678"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        disabled={sendingSms}
                        helperText={translate('resources.tags.helpers.phone_format', 'Include country code')}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !sendingSms && phoneNumber.trim()) {
                                handleSendSmsConfirm();
                            }
                        }}
                    />
                </DialogContent>
                
                <DialogActions>
                    <Button
                        onClick={handleCancelSms}
                        disabled={sendingSms}
                    >
                        {translate('ra.action.cancel')}
                    </Button>
                   
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <MenuItem onClick={() => handleDownload('a4')}>
                    {translate('resources.tags.download.a4')}
                </MenuItem>
                <MenuItem onClick={() => handleDownload('ticket')}>
                    {translate('resources.tags.download.ticket')}
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default QRCodeDisplay;